import { useCallback } from 'react';

import { notifications } from '@mantine/notifications';

import * as Sentry from '@sentry/react';

import { vnextApiClient } from '@/services/vnext-api';
import { ReportRequestAttachment, ReportRequestSource } from '@/types/attachment';
import { toError } from '@/utils/errors';
import { UNCHANGED, usePoll, withEtag } from './usePoll';

export interface UseReportRequestAttachmentsResult {
  attachments: ReportRequestAttachment[];
  sources: ReportRequestSource[];
  isLoading: boolean;
  error: Error | null;
  refetch: () => Promise<void>;
  attach: (conversationAttachmentId: string) => Promise<void>;
  detach: (attachmentId: string) => Promise<void>;
  /** Re-run a failed file digest (async submit flow). */
  retryDigest: (attachmentId: string) => Promise<void>;
  /** Admin: flip a file between Client and Internal. */
  setClassification: (
    conversationAttachmentId: string,
    classification: 'client' | 'internal'
  ) => Promise<void>;
}

/** While any file is still digesting, the list re-polls at this cadence
 * so the rail's chips flip to done/failed without a manual refresh. */
const DIGEST_POLL_INTERVAL_MS = 5_000;

const EMPTY_ATTACHMENTS: ReportRequestAttachment[] = [];
const EMPTY_SOURCES: ReportRequestSource[] = [];

interface AttachmentsSnapshot {
  /** The request the rows belong to, so a stale snapshot never shows
   * under a different request while its fetch is in flight. */
  rrId: string;
  attachments: ReportRequestAttachment[];
  sources: ReportRequestSource[];
}

/**
 * For the RR triage page: the "Attached documents" list (mutable) plus the
 * read-only "Extracted from" provenance list — two distinct relationships.
 */
export function useReportRequestAttachments(
  rrId: string | undefined
): UseReportRequestAttachmentsResult {
  const poll = usePoll<AttachmentsSnapshot>({
    enabled: rrId !== undefined,
    refetchDeps: [rrId],
    // Digest-status poll: only while something is actually in flight.
    interval: (snapshot) =>
      snapshot?.attachments.some((a) => a.digestStatus === 'pending')
        ? DIGEST_POLL_INTERVAL_MS
        : null,
    queryFn: async ({ signal, etag, manual }) => {
      if (!rrId) {
        throw new Error('rrId is required');
      }
      // The digest poll is about the attachments; they carry the ETag. The
      // provenance list is immutable once extracted, so an unchanged
      // attachments set means the snapshot as a whole is unchanged.
      const [attachments, sources] = await Promise.all([
        vnextApiClient.listReportRequestAttachments(rrId, {
          signal,
          etag: manual ? null : etag,
        }),
        vnextApiClient.listReportRequestSources(rrId, { signal }),
      ]);
      if (attachments === 'not-modified') {
        return UNCHANGED;
      }
      return withEtag({ rrId, attachments: attachments.data, sources }, attachments.etag);
    },
    onError: (err) => Sentry.captureException(toError(err)),
  });

  const { refetch: pollRefetch } = poll;
  const refetch = useCallback(
    () => (rrId ? pollRefetch() : Promise.resolve()),
    [rrId, pollRefetch]
  );

  const attach = useCallback(
    async (conversationAttachmentId: string) => {
      if (!rrId) {
        return;
      }
      try {
        await vnextApiClient.attachConversationFileToReportRequest(rrId, conversationAttachmentId);
        await refetch();
      } catch (err) {
        const e = toError(err);
        Sentry.captureException(e);
        notifications.show({ title: 'Attach failed', message: e.message, color: 'red' });
      }
    },
    [rrId, refetch]
  );

  const detach = useCallback(
    async (attachmentId: string) => {
      if (!rrId) {
        return;
      }
      try {
        await vnextApiClient.detachReportRequestAttachment(rrId, attachmentId);
        await refetch();
      } catch (err) {
        const e = toError(err);
        Sentry.captureException(e);
        notifications.show({ title: 'Detach failed', message: e.message, color: 'red' });
      }
    },
    [rrId, refetch]
  );

  const retryDigest = useCallback(
    async (attachmentId: string) => {
      if (!rrId) {
        return;
      }
      try {
        await vnextApiClient.retryReportRequestAttachmentDigest(rrId, attachmentId);
        await refetch();
      } catch (err) {
        const e = toError(err);
        Sentry.captureException(e);
        notifications.show({ title: 'Retry failed', message: e.message, color: 'red' });
      }
    },
    [rrId, refetch]
  );

  const setClassification = useCallback(
    async (conversationAttachmentId: string, classification: 'client' | 'internal') => {
      try {
        await vnextApiClient.updateAttachmentClassification(
          conversationAttachmentId,
          classification
        );
        await refetch();
      } catch (err) {
        const e = toError(err);
        Sentry.captureException(e);
        notifications.show({ title: 'Reclassify failed', message: e.message, color: 'red' });
      }
    },
    [refetch]
  );

  const current = rrId !== undefined && poll.data?.rrId === rrId ? poll.data : undefined;

  return {
    attachments: current?.attachments ?? EMPTY_ATTACHMENTS,
    sources: current?.sources ?? EMPTY_SOURCES,
    isLoading: poll.isLoading,
    error: poll.error === undefined ? null : toError(poll.error),
    refetch,
    attach,
    detach,
    retryDigest,
    setClassification,
  };
}
